import api from '@/api';
import Cookies from '@/portal-cookie';
import { message } from 'antd';
import { getAllLeafKeys } from './buildProductTree';

// Namespaced: every reducer sees every action, so bare type strings shared
// with another module merge foreign payloads into this slice.
let types = {
	SET_STATE: 'MYPRODUCTS/SET_STATE',
	SET_IMAGE: 'MYPRODUCTS/SET_IMAGE',
};


const initialState = {
	cardCode: '',
	cardType: '',
	bpName: '',
	loadedVendorCode: false,

	products: [],
	customerCategories: [],
	loading: false,
	loaded: false,
	catalogError: false,

	// keyed by model_code: { loading } | { base64, mime } | { failed }
	images: {},

	searchText: '',
	checkedKeys: [],
	treeInitialized: false,

	// model_codes of cards click-selected for a combined reorder
	selectedCodes: [],

	reorderOpen: false,
	// [{ ...product, qty }]
	reorderLines: [],
	reorderNotes: '',
	reorderSubmitPending: false,
};


export const reducers = (state = initialState, action) => {
	switch (action.type) {
		case types.SET_STATE:
		{
			return { ...state, ...action.data };
		}
		case types.SET_IMAGE:
		{
			return {
				...state,
				images: {
					...state.images,
					[action.modelCode]: {
						...state.images[action.modelCode],
						...action.data,
					},
				},
			};
		}
		default:
			return state;
	}
};
export default reducers;



export const actions = {
	setState: (data) => ({ type: types.SET_STATE, data }),
	setImage: (modelCode, data) => ({ type: types.SET_IMAGE, modelCode, data }),
	setSearchText: (searchText) => ({ type: types.SET_STATE, data: { searchText } }),
	setCheckedKeys: (checkedKeys) => ({ type: types.SET_STATE, data: { checkedKeys } }),

	loadVendorCode: () => async (dispatch) => {
		try {
			const userKey = Cookies.get('TS_UserKey', '');
			const response = await api.get('/invoices/vendor-code', {
				params: {
					userKey: userKey,
				},
			});

			dispatch({
				type: types.SET_STATE,
				data: {
					loading: false,
					loadedVendorCode: true,
					cardCode: response.BPCode,
					cardType: response.BPType,
					bpName: response.BPName,
				},
			});
		} catch (err) {
			console.error(err);
			dispatch({
				type: types.SET_STATE,
				data: {
					loading: false,
					loadedVendorCode: true,
					cardCode: '',
					cardType: '',
				},
			});
		}
	},

	loadProducts: (forceRefresh = false) => async (dispatch, getState) => {
		const state    = getState(),
			  loading  = state.myProducts.loading,
			  cardCode = state.myProducts.cardCode;

		if (loading) {
			return false;
		}

		// catalogError is only cleared on success so the error stays up (and the
		// auto-retry keeps running) while a background retry is in flight
		dispatch(actions.setState({ loading: true, loaded: false }));

		try {
			let response = await api.get('/products', {
				params: {
					cardCode,
					// asks the service to pop its catalog cache first
					...(forceRefresh ? { refresh: '1' } : {}),
				},
			});

			if (response && response.catalogFromCache) {
				const ageSeconds = Math.round((Date.now() - response.catalogCachedAt) / 1000);
				console.log('[MyProducts] catalog data came from the service cache ('
					+ ageSeconds + 's old); it may be stale if the internal service is down');
			}

			// Most recently ordered first; models missing a date sort last,
			// ties fall back to the label sort
			const products = ((response && response.products) || []).slice().sort((a, b) => {
				const dateA = Date.parse(a.last_ordered_date) || 0;
				const dateB = Date.parse(b.last_ordered_date) || 0;
				if (dateA !== dateB) {
					return dateB - dateA;
				}
				return String(a.model_label || a.model_description || '').localeCompare(String(b.model_label || b.model_description || ''));
			});

			// Drop failed image entries so cards retry them after an outage;
			// a forced refresh drops them all so everything refetches fresh
			const oldImages = getState().myProducts.images;
			const images = {};
			if (!forceRefresh) {
				Object.keys(oldImages).forEach((key) => {
					if (!oldImages[key].failed) {
						images[key] = oldImages[key];
					}
				});
			}

			const customerCategories = (response && response.customer_categories) || [];

			const data = {
				loading: false,
				loaded: true,
				catalogError: false,
				products: products,
				customerCategories: customerCategories,
				images: images,
			};

			// Default every category toggle to on the first time products load
			if (!getState().myProducts.treeInitialized) {
				data.checkedKeys = getAllLeafKeys(products, customerCategories);
				data.treeInitialized = true;
			}

			dispatch({ type: types.SET_STATE, data });
		} catch (err) {
			console.error(err);

			const catalogError = Boolean(
				err && err.response && err.response.status === 503
				&& err.response.data && err.response.data.catalogUnavailable
			);

			dispatch({
				type: types.SET_STATE,
				data: {
					loading: false,
					loaded: true,
					catalogError: catalogError,
					products: [],
				},
			});
		}
	},

	loadProductImage: (modelCode) => async (dispatch, getState) => {
		if (!modelCode) {
			return false;
		}

		const state = getState().myProducts;
		if (state.images[modelCode]) {
			return false;
		}

		dispatch(actions.setImage(modelCode, { loading: true }));

		try {
			const response = await api.get('/products/model-image', {
				params: {
					cardCode: state.cardCode,
					modelCode: modelCode,
				},
			});

			if (response.image && response.image.base64) {
				dispatch(actions.setImage(modelCode, {
					loading: false,
					base64: response.image.base64,
					mime: response.image.mime,
				}));
			} else {
				dispatch(actions.setImage(modelCode, { loading: false, failed: true }));
			}
		} catch (err) {
			if (err && err.response && err.response.status === 503
				&& err.response.data && err.response.data.catalogUnavailable) {
				// Products already on screen came from cache, so no page-level
				// error is shown; the image will retry after the next
				// successful products load
				console.warn('[MyProducts] catalog service unavailable while loading image for '
					+ modelCode + '; continuing with cached product data');
			} else {
				console.error(err);
			}
			dispatch(actions.setImage(modelCode, { loading: false, failed: true }));
		}
	},

	toggleSelectProduct: (modelCode) => (dispatch, getState) => {
		const selectedCodes = getState().myProducts.selectedCodes;
		const next = selectedCodes.includes(modelCode)
			? selectedCodes.filter((code) => code !== modelCode)
			: [...selectedCodes, modelCode];

		dispatch({ type: types.SET_STATE, data: { selectedCodes: next } });
	},

	// Reorder opens with every selected card as a line. With no selection it
	// is just the clicked card; a clicked card outside the selection joins it.
	openReorder: (model) => (dispatch, getState) => {
		const state = getState().myProducts;

		let codes = state.selectedCodes;
		if (!codes.length) {
			codes = [model.model_code];
		} else if (!codes.includes(model.model_code)) {
			codes = [...codes, model.model_code];
		}

		// qty starts empty; the input shows the last ordered amount as a
		// grey placeholder until the user types a quantity
		const lines = state.products
			.filter((product) => codes.includes(product.model_code))
			.map((product) => ({
				...product,
				qty: null,
			}));

		dispatch({
			type: types.SET_STATE,
			data: {
				selectedCodes: state.selectedCodes.length ? codes : state.selectedCodes,
				reorderOpen: true,
				reorderLines: lines,
				reorderNotes: '',
			},
		});
	},

	closeReorder: () => ({
		type: types.SET_STATE,
		data: {
			reorderOpen: false,
			reorderLines: [],
			reorderNotes: '',
		},
	}),

	setReorderLineQty: (modelCode, qty) => (dispatch, getState) => {
		const reorderLines = getState().myProducts.reorderLines.map((line) => (
			line.model_code === modelCode ? { ...line, qty } : line
		));

		dispatch({ type: types.SET_STATE, data: { reorderLines } });
	},

	setReorderNotes: (reorderNotes) => ({ type: types.SET_STATE, data: { reorderNotes } }),

	submitReorderRequest: () => async (dispatch, getState) => {
		const state = getState().myProducts,
			  lines = state.reorderLines;

		if (state.reorderSubmitPending) {
			return false;
		}

		const badLine = lines.find((line) => !line.qty || line.qty < 1);
		if (badLine) {
			message.error(`Please enter a quantity of at least 1 for ${badLine.model_code}`);
			return false;
		}

		dispatch(actions.setState({ reorderSubmitPending: true }));

		try {
			const userKey = Cookies.get('TS_UserKey', '');

			// Only the fields the reorder needs; reorderLines carries the
			// whole product row and the rest does not belong in the request
			await api.post('/products/reorder/send-request', {
				cardCode: state.cardCode,
				cardName: state.bpName,
				userKey: userKey,
				notes: state.reorderNotes,
				lines: lines.map((line) => ({
					model_code: line.model_code,
					model_label: line.model_label,
					model_description: line.model_description,
					last_ordered_qty: line.last_ordered_qty,
					last_ordered_date: line.last_ordered_date,
					last_ordered_docnum: line.last_ordered_docnum,
					qty: line.qty,
				})),
			});

			dispatch(actions.setState({ reorderSubmitPending: false }));
			dispatch(actions.closeReorder());
			message.success('Reorder request submitted. Our team will follow up shortly.');
		} catch (err) {
			console.error(err);

			// Modal stays open so the user keeps their quantities and notes.
			// The api wrapper does not toast server error bodies, so this
			// thunk shows its own.
			dispatch(actions.setState({ reorderSubmitPending: false }));

			const serverErrors = err && err.response && err.response.data && err.response.data.errors;
			message.error((serverErrors && serverErrors[0])
				|| 'Could not submit your reorder request. Please try again later.');
		}
	},

};
